import { Injectable } from '@angular/core';
import { QrCodeService } from '@shared/services/qr-code.service';
import { ApiResponse, QR_CODE_TYPE } from '@shared/models';
import { Router } from '@angular/router';
import { HelperService } from '@shared/helpers/helper.service';
import { LcoRoutesMap } from '../lco-routing.module';
import { UserService } from '@shared/services/user.service';
import { BehaviorSubject, interval, Observable, of, timer } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { filter, tap, timeout } from 'rxjs/operators';
import { environment } from '@env/environment';
import { GuestData } from '../models/guest-data';
import { WebsocketService } from '@shared/services/websocket.service';
import { LoggerService } from '@shared/services/logger.service';

// todo LCO_MEMBER

const TACIT_TOKEN_UPDATE_INTERVAL = 60000; // ms

export interface QrCodeCheckinDto {
  locationId: string,
  tableNumber: number,
  phone: string,
  guid: string
}

@Injectable({
  providedIn: 'root'
})
export class LcoService {
  partySizeSubject = new BehaviorSubject(0);
  guestDataSubject = new BehaviorSubject([]);
  env = environment;
  checkedIn$ = new BehaviorSubject(false);
  tableSet$ = new BehaviorSubject(false);
  lcoIframeCredentials: BehaviorSubject<any> = new BehaviorSubject(null);

  get partySize() {
    return this.partySizeSubject.getValue();
  }

  set partySize(value: number) {
    this.partySizeSubject.next(value);
  }

  get guestData() {
    return this.guestDataSubject.getValue();
  }

  set guestData(value: any) {
    this.guestDataSubject.next(value);
  }

  get isLco(): boolean {
    return !!(this.qrCodeService.parsedQrCode && this.qrCodeService.parsedQrCode.type === QR_CODE_TYPE.TABLE)
  }

  get checkedIn() {
    return this.checkedIn$.getValue();
  }

  constructor(private qrCodeService: QrCodeService,
              private router: Router,
              private helper: HelperService,
              private userService: UserService,
              private http: HttpClient,
              private websocketService: WebsocketService,
              private logger: LoggerService) {
  }

  lcoHomePage() {
    this.router.navigate([LcoRoutesMap.base]);
  }

  handleLcoRouting() {
    const route = this.getRoute();
    if (route) {
      this.router.navigate([`${route}`], {replaceUrl: true});
    } else {
      this.helper.redirectToFallbackPage();
    }
  }

  async updateLcoIframeCredentials() {
    // if (!this.checkedIn$.getValue()) {
    //   return;
    // }
    const credentials = await this.getLcoIframeCredentials().toPromise();

    if (!credentials || !credentials.payload || typeof credentials.payload === 'string') {
      return;
    }

    this.logger.log(`updateLcoIframeCredentials credentials: `, credentials);

    await timer(TACIT_TOKEN_UPDATE_INTERVAL).toPromise();
    this.updateLcoIframeCredentials();
  }

  // async getLcoIframeCredentials() {
  //   const credentials: any = await this.getLcoIframeCredentials().toPromise();
  //   this.logger.log(`updateLcoData iframe credentials after websocket response`, credentials);
  //
  //   if (credentials.payload) {
  //     this.checkedIn$.next(true);
  //     this.tableSet$.next(true);
  //   }
  // }

  async updateLcoData() {
    this.logger.log(`updateLcoData ------------------------------------------`);

    if (!this.userService.isAuthorized) {
      return true;
    }

    // todo model
    // get credentials
    let credentials: any = await this.userService.getLcoCredentials().toPromise();
    credentials = credentials.payload || null;

    this.logger.log(`updateLcoData credentials`, credentials);

    if (credentials) {
      if (!this.userService.verifiedPhone) {
        return true;
      }

      if (this.checkedIn$.getValue() && this.tableSet$.getValue()) {
        return;
      }

      this.logger.log(`updateLcoData phoneVerified`);

      // subscribe to order status websocket
      if (!this.websocketService.rxStomp?.connected()) {
        this.websocketService.setup();
      }

      this.logger.log(`updateLcoData before websocket`);

      this.websocketService.orderStatus$(credentials.mobileNumber)
        .subscribe(async (res) => {
          const orderStatus = JSON.parse(res.body).payload;

          this.logger.log(`updateLcoData websocketService orderStatus$ response`, JSON.parse(res.body).payload);

          // if (orderStatus.phoneNumber) {
            this.updateLcoIframeCredentials();
          // }
        });


      // todo model
      // checkin tacit->squirrel QR code
      const qrCodeCheckin: any = await this.qrCodeCheckin().toPromise();

      this.logger.log(`qrCodeCheckin`, qrCodeCheckin);


      // await this.getLcoIframeCredentials().toPromise();
      // this.logger.log(`updateLcoData iframe credentials after websocket response`, credentials);
      // this.lcoIframeCredentials.next(credentials);
    }

    return true;
  }

  getRoute() {
    this.logger.log(`getRoute ----------->>>`);

    let route = '';
    const routingState = this.helper.routingState;

    if (this.userService.isAuthorized) {
      if (this.checkedIn$.getValue() && this.tableSet$.getValue()) {
        route = ''; // redirect to home page
      } else if (this.userService.verifiedPhone) {
        if (!routingState.lco.partySize || !this.partySizeSubject.getValue()) {
          route = LcoRoutesMap.partySize;
        } else if (!routingState.lco.guestsNames) {
          route = LcoRoutesMap.guestsData;
        }
      } else {
        if (!routingState.lco.getStarted) {
          route = LcoRoutesMap.getStarted;
        } else if (!routingState.phoneConfirm) {
          route = LcoRoutesMap.phoneConfirmation;
        } else {
          route = LcoRoutesMap.getStarted;
        }
      }
    } else {
      if (!routingState.lco.getStarted) {
        route = LcoRoutesMap.getStarted;
      } else if (!routingState.phoneConfirm) {
        route = LcoRoutesMap.phoneConfirmation;
      } else {
        route = LcoRoutesMap.getStarted;
      }
    }

    this.logger.log(`getRoute route: `, route);

    return `${LcoRoutesMap.base}/${route}`;
  }

  goToNextPage() {
    this.handleLcoRouting();
  }

  qrCodeCheckin() {
    const url = `${this.env.server}lco/qr_check_in`;

    const qrCodeCheckinDto: QrCodeCheckinDto = {
      locationId: this.userService.location.id,
      tableNumber: 18, // this.qrCodeService.parsedQrCode?.tableNumber, // todo change hardcoded value after Tacit fix their API
      phone: this.userService.userMobilePhone,
      guid: ''
    }

    return this.http.post(url, qrCodeCheckinDto)
  }

  getLcoIframeCredentials() {
    const url = `${this.env.server}lco/checkin/info`;
    return this.http.post(url, {}).pipe(
      tap((credentials: any) => {
        this.logger.log(`getLcoIframeCredentials credentials: `, credentials);

        if (credentials.payload && typeof credentials.payload !== 'string') {
          this.lcoIframeCredentials.next(credentials.payload);

          if (!this.checkedIn$.getValue()) {
            this.checkedIn$.next(true);
            this.tableSet$.next(true);
          }
        } else {
          this.checkedIn$.next(false);
          this.tableSet$.next(false);
        }
      })
    )
  }

  addGuests(guests: GuestData[]): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.env.server}lco/guest/add`, guests)
      .pipe(
        tap((data) => {
          this.logger.log(data);
        })
      );
  }
}
